import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Home, Briefcase, Sparkles, GraduationCap, Zap, Users, Heart, Send, Brain } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { useAgentProfile } from "@/hooks/useAgentProfile";
import { AgentOnboarding } from "@/components/askCircle/AgentOnboarding";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { requireUser } from "@/lib/requireUser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import circleLogoUrl from "@/assets/circle-network-logo.png";

const CATEGORIES = [
  { name: 'CRM', icon: Home, color: 'bg-blue-100 hover:bg-blue-200', iconColor: 'text-blue-600', slug: 'crm' },
  { name: 'Marketing Tools', icon: Briefcase, color: 'bg-sky-100 hover:bg-sky-200', iconColor: 'text-sky-600', slug: 'marketing' },
  { name: 'Lead Generation', icon: Sparkles, color: 'bg-pink-100 hover:bg-pink-200', iconColor: 'text-pink-600', slug: 'lead-generation' },
  { name: 'Real Estate Schools', icon: GraduationCap, color: 'bg-green-100 hover:bg-green-200', iconColor: 'text-green-600', slug: 'education' },
  { name: 'Licensing', icon: Zap, color: 'bg-orange-100 hover:bg-orange-200', iconColor: 'text-orange-600', slug: 'licensing' },
  { name: 'Coaching', icon: Users, color: 'bg-purple-100 hover:bg-purple-200', iconColor: 'text-purple-600', slug: 'coaching' },
  { name: 'Marketplace', icon: Heart, color: 'bg-yellow-100 hover:bg-yellow-200', iconColor: 'text-yellow-600', slug: 'marketplace' },
];

const AskCircle = () => {
  const { user } = useAuth();
  const { profile, isLoading: profileLoading } = useAgentProfile();
  const { openChat } = useChat();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  useEffect(() => {
    if (!isFeatureEnabled('askCircle')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (user && profile && !profile.onboarding_completed) {
      setShowOnboarding(true);
    }
  }, [user, profile]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      openChat(searchInput.trim());
    }
  };

  const handleCategoryClick = (categoryName: string) => {
    if (!user) {
      requireUser({
        user,
        navigate,
        currentPath: '/ask-circle',
      });
      return;
    }
    const question = `I am looking for a new ${categoryName.toLowerCase()}`;
    setSearchInput(question);
    openChat(question);
  };

  const handleInputFocus = () => {
    if (!user) {
      requireUser({
        user,
        navigate,
        currentPath: '/ask-circle',
      });
    }
  };

  // Loading profile (only for authenticated users)
  if (user && profileLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  // Show onboarding if not completed (only for authenticated users)
  if (user && showOnboarding) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 py-8">
          <AgentOnboarding onComplete={handleOnboardingComplete} />
        </main>
        <Footer />
      </div>
    );
  }

  // Main interface - shown to all users
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 md:py-16">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Hero Section */}
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center gap-3">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-blue-600">
                  Need Advice?
                </h1>
                <Badge className="bg-yellow-400 text-yellow-900 hover:bg-yellow-500 text-sm">BETA</Badge>
              </div>
              
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Built on real purchase data from top agents. Buy most everything at member pricing and choose what actually works. No cold calls. No spam. Clear choices you can trust. Advice you can lean on.
              </p>
            </div>

            {/* Search Input Section */}
            <form onSubmit={handleSearchSubmit} className="space-y-3">
              <div className="relative">
                <Brain className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-blue-theme" />
                <Input
                  type="text"
                  placeholder={user ? "Type your question..." : "We have a few questions to better assist you..."}
                  value={user ? searchInput : ''}
                  onChange={user ? (e) => setSearchInput(e.target.value) : undefined}
                  onFocus={handleInputFocus}
                  readOnly={!user}
                  className="pl-14 pr-24 h-14 text-base rounded-full cursor-pointer"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Button
                    type="submit"
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    disabled={!user || !searchInput.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Try: <span className="text-foreground font-medium">"best CRM for 20 deals a year"</span>
              </p>
            </form>

            {/* Category Icons */}
            <div className="space-y-6">
              <div className="grid grid-cols-3 md:grid-cols-7 gap-4">
                {CATEGORIES.map((category) => (
                  <button
                    key={category.slug}
                    onClick={() => handleCategoryClick(category.name)}
                    className="flex flex-col items-center gap-2 group"
                  >
                    <div className={`${category.color} p-4 rounded-full transition-colors`}>
                      <category.icon className={`h-6 w-6 ${category.iconColor}`} />
                    </div>
                    <span className="text-xs font-medium text-center">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Book CTA */}
            <div className="text-center">
              <Button 
                size="lg" 
                variant="outline"
                className="gap-2"
                onClick={() => setIsBookingOpen(true)}
              >
                <Sparkles className="h-5 w-5" />
                Book with an Agent Concierge
              </Button>
            </div>

            {/* Help Text */}
            <Card className="p-6 bg-muted/50">
              <p className="text-sm text-muted-foreground text-center">
                <strong>Not sure where to start?</strong> Ask something specific like "How do I get to 100 deals a year from 20 in Nashville TN" and we will map the proven path based on real agent data
              </p>
            </Card>

            {/* Footer Text */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              <span>Advice powered by curated data and Live Agent Interviews</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Booking Modal */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>Book with an Agent Concierge</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[600px]">
            <iframe
              src="https://link.circlehub.org/widget/booking/XMerHP4ILIgxbU9odtIS"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Book a call with Agent Concierge"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AskCircle;
